import axios from "axios";
import Cookies from "js-cookie";
import { performLogout } from "../utils/LogoutHandle";
import { store } from "../Redux/Store";
import { GetCookies } from "../utils/GetCookies";

////console.log("process.env.REACT_APP_API_URL",process.env.REACT_APP_API_URL);
const BASE_URL = process.env.REACT_APP_API_URL;

// Public axios instance (no authorization)
const publicApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Private axios instance (with authorization)
const privateApi = axios.create({
  baseURL: BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Comparables API instance (separate backend, token-based auth)
const comparablesApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
    "X-Access-Token": process.env.REACT_APP_COMPARABLES_ACCESS_TOKEN,
  },
});

// Request interceptor for privateApi to add access token to headers
privateApi.interceptors.request.use(
  (config) => {
    let { accessToken } = GetCookies();
    config.headers["Authorization"] = `Bearer ${accessToken}`;
    // Chatbot analytics aggregates by the viewer's local timezone: send the
    // browser's IANA zone (e.g. "America/Los_Angeles") so the backend buckets
    // days/hours in local time instead of UTC.
    if (
      typeof config.url === "string" &&
      config.url.includes("/chat-bot/analytics/")
    ) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) config.params = { ...(config.params || {}), tz };
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const handleLogout = () => {
  const dispatch = store.dispatch;
  performLogout(dispatch);
};

privateApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // error.response is undefined for network errors and timeouts. Dereferencing
    // it threw a TypeError *inside* the interceptor, replacing the real axios
    // error with an unrelated one — which made every offline failure confusing.
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;
      const refreshed = await refreshAccessTokenOnce();
      ////console.log("refreshed", refreshed);
      if (refreshed) {
        let { accessToken } = GetCookies();
        originalRequest.headers["Authorization"] = `Bearer ${accessToken}`;
        return privateApi(originalRequest);
      } else {
        handleLogout();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Single-flight wrapper around refreshAccessToken.
 *
 * Without it, N concurrent 401s fire N refresh calls — each racing to overwrite
 * the access_token cookie, and all but one of them working from a refresh token
 * the server may already have rotated. A live console that holds a socket open
 * and polls on reconnect makes that easy to hit.
 */
let refreshPromise = null;
export function refreshAccessTokenOnce() {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

// Function to refresh the access token
export async function refreshAccessToken() {
  let { accessToken, refreshToken } = GetCookies();
  try {
    let formData = { refresh: refreshToken };
    const response = await publicApi.post("/accounts/auth/refresh/", formData, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${refreshToken}`,
      },
    });

    if (response.status === 200) {
      accessToken = response.data.access;
      ////console.log("accessToken response",response.data);
      Cookies.set("access_token", accessToken, { expires: 1 / 24 });
      return true;
    } else {
      // console.error('Refresh token is invalid');
      return false;
    }
  } catch (error) {
    // console.error('Error refreshing token:', error);
    return false;
  }
}

export const handleGetProperties = async () => {
  try {
    const response = await axios.get("/db/response_5mired.json");
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetSearchAndCompareUnits = async (queryString) => {
  try {
    const response = await privateApi.get(`/property/search/?${queryString}`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetIndependentComparables = async (payload) => {
  try {
    const response = await comparablesApi.post(
      "/property/comparables/list/",
      payload
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPropertyByAddress = async (payload) => {
  try {
    const response = await comparablesApi.post(
      "/property/comparables/property-by-address/",
      payload
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetNearbyComparables = async (payload) => {
  try {
    const response = await comparablesApi.post(
      "/property/comparables/nearby-list/",
      payload
    );
    return response;
  } catch (error) {
    return error;
  }
};

// Change 3: fetch a single replacement comparable after a card is deleted.
// Pass the same search payload with exclude_properties containing all currently
// visible addresses — the backend returns the next best match.
export const handleGetReplacementComparable = async (payload) => {
  try {
    const response = await comparablesApi.post(
      "/property/comparables/nearby-list/",
      payload
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPropertyDetailsAndCompareUnits = async (
  id,
  queryString
) => {
  try {
    const response = await privateApi.get(
      `/property/compare/${id}/?${queryString}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetUnitsPropertyDetailsAndCompare = async (
  id,
  queryString
) => {
  try {
    const response = await privateApi.get(
      `/property/compare-units/${id}/?${queryString}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetBellcurveChartData = async (id, query) => {
  try {
    const response = await privateApi.get(
      `property/units-drilldown/bellcurve/${id}/?top_count=${query}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetBarMedianChartData = async (id) => {
  try {
    const response = await privateApi.get(
      `property/units-drilldown/barchart/${id}/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPropertySearch = async (queryString) => {
  try {
    const response = await privateApi.get(
      `/property/properties/search/?${queryString}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPropertySearchFilter = async () => {
  try {
    const response = await privateApi.get(
      `/property/properties/property/filter-property-search/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPortfolioViewDetails = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/property/drill-down/${id}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

//Drilldown Lease Expiring Report
export const handleGetLeaseExpriringReportDetails = async (id, queryString) => {
  try {
    const response = await privateApi.get(
      `property/properties/property/drill-down-lease-expiry/${id}/?${queryString}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetLeaseExpriringPropertiesChart = async (id) => {
  try {
    const response = await privateApi.post(
      `property/properties/property/drill-down-lease-expiry-chart/${id}/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetDemandSignalChartDetails = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/property/demand-signal-chart/${id}/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdateProposedRent = async (id, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property/unit-update-proposed-rent/${id}/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleLogin = async (data) => {
  try {
    const response = await publicApi.post(`/accounts/auth/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleSuperuserLogin = async (data) => {
  try {
    const response = await publicApi.post(`/accounts/auth/superuser/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleForgotPassword = async (data) => {
  try {
    const response = await publicApi.post(`/accounts/reset-password/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleVerifyPasswordToken = async (data) => {
  try {
    const response = await publicApi.post(
      `/accounts/reset-password/verify_token/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleResetPassword = async (data) => {
  try {
    const response = await publicApi.post(
      `/accounts/reset-password/verify/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleChangePassword = async (data) => {
  try {
    const response = await privateApi.post(`/accounts/change-password/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdateProfile = async (data) => {
  try {
    const response = await privateApi.put(
      `property/manager/profile/update/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handlePropertyManagerInvite = async (data) => {
  try {
    const response = await privateApi.post(
      `property/manager/pm_admin/invite/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdatePropertyManagerInvite = async (data) => {
  try {
    const response = await privateApi.post(
      `property/manager/pm_admin/disable-update-user/ `,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handlePropertyManagerInviteList = async () => {
  try {
    const response = await privateApi.post(
      `property/manager/pm_admin/pminvite/list/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleVerifyInvite = async (token) => {
  try {
    const response = await publicApi.get(
      `/property/manager/pm_admin/invite/verify/${token}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPropertyGroups = async () => {
  try {
    const response = await privateApi.get(`/property/groups/list/`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetPropertyGroupsList = async () => {
  try {
    const response = await privateApi.get(
      `property/manager/pm_admin/property-group-list/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handlePropertyManagerCreate = async (data) => {
  try {
    const response = await publicApi.post(
      `property/manager/profile/create/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetFaqsList = async (data) => {
  try {
    const response = await privateApi.post(`admin/faq/list/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetFaqsCategoryList = async () => {
  try {
    const response = await privateApi.get(`admin/faq/category/list/`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleApiCredentialsCreate = async (data) => {
  try {
    const response = await privateApi.post(
      `property/admin/appfolio/create/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetApiCredentials = async () => {
  try {
    const response = await privateApi.get(`property/admin/appfolio/get/`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handlePullData = async () => {
  try {
    const response = await privateApi.post(`property/appfolio/pull_data/`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleApiCredentialsUpdate = async (data) => {
  try {
    const response = await privateApi.put(
      `property/admin/appfolio/update/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetFeedbackCategoryList = async () => {
  try {
    const response = await privateApi.get(`/admin/support-category/list/`);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleCreateSupportFeedback = async (data) => {
  try {
    const response = await privateApi.post(`/admin/feedbacks/create/`, data);
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdatePropertyDetails = async (id, data) => {
  try {
    const response = await privateApi.put(
      `property/appfolio/property/update/${id}/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleRemovePropertyComparables = async (data) => {
  try {
    const response = await privateApi.post(
      `property/appfolio/unit/comparablesproperty/add/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleAddPropertyComparables = async (data) => {
  try {
    const response = await privateApi.post(
      `property/appfolio/unit/comparablesproperty/undo/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdatePropertyComparables = async (data) => {
  try {
    const response = await privateApi.post(
      `property/property/update-addon-ls/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUpdateTotalUnitsInComparables = async (data) => {
  try {
    const response = await privateApi.post(
      `property/property/update-addon-total-units/`,
      data
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetMissingFields = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/appfolio/property/missing_fields/?${queryString}`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleGetChatCommentsList = async (id) => {
  try {
    const response = await privateApi.post(
      `property/appfolio/unit/comment/list/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleAddChatComments = async (id, data) => {
  try {
    const response = await privateApi.post(
      // `property/owner-comm/create-unit-comment/${id}/`,
      `property/appfolio/unit/comment/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatCommentsListPublic = async (id, requestId) => {
  try {
    const url = requestId
      ? `property/owner-comm/get-unit-comments/${id}/?request_id=${requestId}`
      : `property/owner-comm/get-unit-comments/${id}/`;
    const response = await publicApi.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatCommentsListOwner = async (id) => {
  try {
    const response = await privateApi.get(
      `property/owner-comm/get-unit-comments/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleAddChatCommentsPublic = async (id, data) => {
  try {
    const response = await publicApi.post(
      `property/owner-comm/create-unit-comment/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleQueueOwnerEmail = async (data) => {
  try {
    const response = await privateApi.post(
      `property/owner-comm/send-email/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOwnerEmailQueueSummary = async () => {
  try {
    const response = await privateApi.get(
      `property/owner-comm/email-queue-summary/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleProcessOwnerEmailQueue = async (data) => {
  try {
    const response = await privateApi.post(
      `property/owner-comm/queue-email-for-property/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetComparablesUnitDetails = async (id) => {
  try {
    const response = await privateApi.get(
      `property/property/property_ls_separate/${id}/`
    );
    return response;
  } catch (error) {
    return error;
  }
};

export const handleUploadPropertyImport = async (formData) => {
  try {
    const response = await privateApi.post(
      "property/appfolio/import-property/",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetLeaseAnalysisChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/properties/lease-up-analysis/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//Lease Analysis Report
export const handleGetLeaseAnalysisReport = async (query, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/lease-up-analysis-units/?${query}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//Vacancy Analysis Report
export const handleGetVacancyAnalysisReport = async (query, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/vacant-analysis/units/?${query}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDashboardCardDetails = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/dashboard/min-values/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetAvgRentByFloorPlan = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/dashboard/bedroom-rent-chart/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDashboardVacanctReport = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/dashboard/vacant-report/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetVacanctAnalysisChart = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/vacant-analysis/bar-chart/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetVacantChartByVacancy = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/vacant-analysis/bar-unit-summary/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDashboardVacanctReportChart = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/dashboard/vacant-report-chart/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDashboardVacanctPieChart = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/vacant-analysis/pie-chart/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDashboardRenewedLeases = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/dashboard/renwed-pie-chart/
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquentPieChart = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/delinquency/piechart/?${query}
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquentStackChart = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/delinquency/stacked-bar-chart/?${query}
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetStackChartByDeliquent = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/delinquency/stacked-bar-unit-summary/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquentReportData = async (query, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/delinquency/analysis-report/?${query}
`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetManageUnits = async (pageNum, query) => {
  try {
    const response = await privateApi.get(
      `property/properties/exclude-units-customer/units-list/?page=${pageNum}&page_size=35&query=${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleEnableDisableUnits = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/exclude-units-customer/update-unit/
`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleBulkEnableDisableUnits = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/exclude-units-customer/update-unit/
`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetAiInsightsForecastChart = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/forecast-chart/${id}
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetVacantAiInsightsForecastChart = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/forecasted-vacant-chart/${id}
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetAiInsightsTenantFunnel = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/guest-card-chart/${id}/
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetHighLowChart = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/high-low-chart/
`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetSupplyInfoChart = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/demand-supply-mile-chart/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetRentedOutAiInsights = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/rented-out/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetRentedOutDrillDown = async (id) => {
  try {
    const response = await privateApi.get(
      `property/units/rented-out-unit/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetTopTenWorstProperties = async (query) => {
  try {
    const response = await privateApi.get(
      `property/properties/trends/monthly-top/?mode=${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateProposedRentExpiration = async (query) => {
  try {
    const response = await privateApi.get(
      `property/appfolio/clear_old_proposed_rent/?mode=${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetRulePropertiesLists = async () => {
  try {
    const response = await privateApi.get(`property/rules/list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleAddRulesProperties = async (data) => {
  try {
    const response = await privateApi.post(`property/rules/add-rules/`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetExistingCreatedRules = async () => {
  try {
    const response = await privateApi.get(`property/rules/list-rules/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteRules = async (id) => {
  try {
    const response = await privateApi.get(`property/rules/delete-rules/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Vacancy Insights

export const handleGetVacancyInsightsDetails = async (id) => {
  try {
    const response = await privateApi.get(
      `property/properties/ai-insight/full-details/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//Rent Specials

export const handleGetRentalSpecials = async (id) => {
  try {
    const response = await privateApi.get(
      `property/units-drilldown/rent-specials/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//Other Recurring fee

export const handleGetRecurringFeeDetails = async (id, type) => {
  try {
    const response = await privateApi.post(
      `property/units/list/${id}/?type=${type}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateRecurringFees = async (data, type) => {
  try {
    const response = await privateApi.post(
      `property/units/update/proposed-unit-fees/?type=${type}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateCurrentUnitFees = async (data) => {
  try {
    const response = await privateApi.post(
      `property/units/update/current-unit-fees/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//this is concession days api
export const handleUpdateConcessionInDays = async (id, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property/unit-update-concessions-in-days/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//================================================== Leasing Overview ======================================================

export const handleGetLeasingOverviewDetails = async (queryString, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/lease-renewal/?${queryString}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOccupancyAnalysisChart = async (queryString, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/occupancy-rate/bar-chart/?${queryString}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetFunnelAnalyticsChart = async (queryString, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/funnel-analytics-chart/?${queryString}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//=============================================== Custom Column ===============================================

export const handleCreateCustomColumn = async (data) => {
  //Payload = field_name, field_type, if field_type is dropdown = "options" else no keyword
  try {
    const response = await privateApi.post(`property/unit/create/`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCustomColumn = async () => {
  try {
    const response = await privateApi.get(`property/unit/list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateCustomColumn = async (data) => {
  //Payload = unit_id, field_id, value,
  try {
    const response = await privateApi.post(
      `property/unit/create/custom-field-value/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteCustomColumn = async (data) => {
  //Payload = field_id,
  try {
    const response = await privateApi.post(
      `property/unit/delete/custom-field/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetVacantCustomColumn = async () => {
  try {
    const response = await privateApi.get(`property/unit/list-vacant/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateVacantCustomColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/create-vacant/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateVacantCustomColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/create/custom-field-value-vacant/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteVacantCustomColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/delete/custom-field-vacant/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquencyCustomColumn = async () => {
  try {
    const response = await privateApi.get(`property/unit/list-delinquency/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateDelinquencyCustomColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/create-delinquency/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateDelinquencyCustomColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/create/custom-field-value-delinquency/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteCustomFieldOption = async (data) => {
  try {
    const response = await privateApi.post(
      `property/unit/delete/custom-field-option/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSoftDeleteCustomColumn = async (data) => {
  // Payload: { field_id, source } — source is "vacant" or "lease"
  try {
    const response = await privateApi.post(
      `property/unit/soft-delete/custom-field/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};
export const handleUpdateCustomField = async (data) => {
  try {
    const response = await privateApi.post(`property/unit/update-field/`, data);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateAvailableOn = async (id, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/vacant-analysis/update-customer-available-on-date/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================================== Expense Ratio ============================================

export const handleGetExpenseRatioChartData = async (query) => {
  try {
    const response = await privateApi.get(
      `property/corporate-accounting/expense-bar-chart/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetExpenseRatioPropertyDetails = async (query) => {
  try {
    const response = await privateApi.get(
      `property/corporate-accounting/expense-bar-list/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetFinancialFiltersListYears = async () => {
  try {
    const response = await privateApi.get(
      `property/corporate-accounting/filters/list-years/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetAccountingFiltersList = async () => {
  try {
    const response = await privateApi.get(
      `property/corporate-accounting/expense-ratio-filters/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOccupancyRateList = async (id) => {
  try {
    const response = await privateApi.post(
      `property/properties/occupancy-rate/list/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSaveOccupancyRate = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/occupancy-rate/save-value/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateRecentlyRentedComps = async (id, data) => {
  try {
    const response = await privateApi.post(
      `property/update-recently-rented-comps/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPropertyFeeDetails = async (id) => {
  try {
    const response = await privateApi.post(`property/units/${id}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquentCustomers = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/delinquency/top-10-customer/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetMTMTrendsList = async () => {
  try {
    const response = await privateApi.post(
      `property/properties/property-mtm-count/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetDelinquencyTrend = async () => {
  try {
    const response = await privateApi.get(
      `property/properties/delinquency/top-10-trend/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetLeaseExpiryVacancyChart = async (queryString, data) => {
  try {
    const response = await privateApi.post(
      `property/properties/vacancy-days-bar-chart/?${queryString}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= PROFIT AND LOSS V1 =======================================

export const handleGetPNLFiltersList = async () => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/filters-list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLPropertiesList = async (queryString = "") => {
  try {
    const endpoint = queryString
      ? `property/pnl-statement/properties-list/?${queryString}`
      : `property/pnl-statement/properties-list/`;
    const response = await privateApi.get(endpoint);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLPropertyGroupsList = async () => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/property-groups-list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLAccountsList = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/accounts-list/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetManagerProfilePic = async () => {
  try {
    const response = await privateApi.get(`property/manager/profile/pic-url/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLBarChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/bar-chart/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLPieChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/pie-chart/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================================== P&L Comments V1 ===============================================

export const handleGetPNLCellComments = async (cellId, mode) => {
  try {
    const queryParams = mode ? `?mode=${mode}` : "";
    const response = await privateApi.get(
      `property/pnl-statement/get-cell-comments/${cellId}${queryParams}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLRowComments = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/get-row-comments/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPNLPinnedComments = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/pnl-statement/get-pinned-comments/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreatePNLComment = async (cellId, data) => {
  try {
    const response = await privateApi.post(
      `property/pnl-statement/create-comment/${cellId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdatePNLComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/pnl-statement/update-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeletePNLComment = async (commentId) => {
  try {
    const response = await privateApi.delete(
      `property/pnl-statement/delete-comment/${commentId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePinPNLComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/pnl-statement/pin-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= OWNER REVIEW =======================================

export const handleGetOwnerReviewData = async (id) => {
  try {
    const response = await publicApi.get(
      `property/owner-comm/owner-review/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOwnerUnitDetails = async (unitId, queryString) => {
  try {
    let url = `property/owner-comm/owner-unit-details/${unitId}/`;
    if (queryString) {
      url += `?${queryString}`;
    }
    const response = await publicApi.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOwnerCommUnitsList = async (data) => {
  try {
    const response = await privateApi.post(
      `property/owner-comm/units-list/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOwnerReviewBellcurve = async (id, query) => {
  try {
    const response = await privateApi.get(
      `property/owner-review/bellcurve/${id}/?top_count=${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateOwnerReviewStatus = async (id, data) => {
  try {
    const response = await publicApi.post(
      `property/owner-comm/unit-review/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= MANAGER REVIEW =======================================

export const handleGetManagerReviewChart = async (id) => {
  try {
    const response = await privateApi.get(
      `property/manager-review/chart/${id}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetManagerReviewTable = async (id, query) => {
  try {
    const response = await privateApi.post(
      `property/manager-review/units/${id}/?${query}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetFeeDetails = async (unitId) => {
  try {
    const response = await privateApi.get(`property/fee-details/${unitId}/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetOwnerCommUnitsBarChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/owner-comm/units-bar-chart/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateOwnerEmail = async (data) => {
  try {
    const response = await privateApi.patch(
      `property/owner-comm/update-owner-emails/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetPropertyAppfolioOwners = async (unitId) => {
  try {
    const response = await privateApi.get(
      `property/owner-comm/property-appfolio-owners/?unit_id=${unitId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSendOwnerReviewEmail = async (data) => {
  try {
    const response = await privateApi.post(
      `property/owner-comm/send-email/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};
export const handleUpdateOwnerCommDueDate = async (id, data) => {
  try {
    const response = await privateApi.patch(
      `property/owner-comm/update-due-date/${id}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= CASHFLOW V1 =======================================

export const handleGetCashflowFiltersList = async () => {
  try {
    const response = await privateApi.get(`property/cashflow/filters-list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowPropertiesList = async () => {
  try {
    const response = await privateApi.get(`property/cashflow/properties-list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowAccountsList = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/cashflow/accounts-list/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowBarChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/cashflow/bar-chart/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowPieChart = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/cashflow/pie-chart/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================================== Cashflow Comments V1 ===============================================

export const handleGetCashflowCellComments = async (cellId, mode) => {
  try {
    const queryParams = mode ? `?mode=${mode}` : "";
    const response = await privateApi.get(
      `property/cashflow/get-cell-comments/${cellId}${queryParams}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowRowComments = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/cashflow/get-row-comments/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetCashflowPinnedComments = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/cashflow/get-pinned-comments/?${queryString}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateCashflowComment = async (cellId, data) => {
  try {
    const response = await privateApi.post(
      `property/cashflow/create-comment/${cellId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateCashflowComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/cashflow/update-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteCashflowComment = async (commentId) => {
  try {
    const response = await privateApi.delete(
      `property/cashflow/delete-comment/${commentId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePinCashflowComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/cashflow/pin-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot Widget Settings ===============================================

// True when a scoped chatbot call was rejected because the website_id no longer
// resolves (e.g. deleted website). Callers use this to reset the scope to "All".
export const isInvalidWebsiteError = (error) => {
  if (error?.response?.status !== 400) return false;
  const msg =
    error?.response?.data?.error || error?.response?.data?.message || "";
  return /invalid\s*_?\s*website/i.test(msg);
};

export const handleGetPropertiesDropdown = async () => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/properties-dropdown/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetWidgetSettings = async (
  propertyId = null,
  websiteId = null
) => {
  try {
    // Widget get/save/load resolve WEBSITE first server-side (opposite of the
    // analytics endpoints, which prefer property). We sidestep that by sending
    // exactly one scope key — property-first here purely to guarantee a single
    // key, never both.
    let body = {};
    if (propertyId != null) body = { property_id: propertyId };
    else if (websiteId != null) body = { website_id: websiteId };
    const response = await privateApi.post(
      `property/chat-bot/widget-settings/get/`,
      body
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSaveWidgetSettings = async (data) => {
  try {
    const response = await privateApi.post(
      `property/chat-bot/widget-settings/save/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleLoadWidgetSettingsPublic = async (
  externalOrgId,
  externalPropertyId = null,
  externalWebsiteId = null
) => {
  try {
    // Widget load resolves WEBSITE first server-side; we send exactly one scope
    // key (property-first) so precedence never actually matters. Never both.
    let qs = `?external_organization_id=${externalOrgId}`;
    if (externalPropertyId) qs += `&external_property_id=${externalPropertyId}`;
    else if (externalWebsiteId)
      qs += `&external_website_id=${externalWebsiteId}`;
    const response = await publicApi.get(
      `property/chat-bot/widget-settings/load/${qs}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot Knowledge Base ===============================================

// Scope params for the KB endpoints. Property and website are mutually
// exclusive, so we emit exactly one key (property-first) — both null means org
// ("All") scope. GET/DELETE spread this into query params; POST/PUT into the
// body. Mirrors the single-key convention of handleGetWidgetSettings.
const kbScopeParams = (propertyId = null, websiteId = null) => {
  if (propertyId != null) return { property_id: propertyId };
  if (websiteId != null) return { website_id: websiteId };
  return {};
};

export const handleGetQA = async (propertyId = null, websiteId = null) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/knowledge-base/get-qa/`,
      { params: kbScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpsertQA = async (
  items,
  propertyId = null,
  websiteId = null
) => {
  try {
    const response = await privateApi.post(
      `property/chat-bot/knowledge-base/upsert-qa/`,
      { items, ...kbScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteQA = async (
  pk,
  propertyId = null,
  websiteId = null
) => {
  try {
    const response = await privateApi.delete(
      `property/chat-bot/knowledge-base/delete-qa/${pk}/`,
      { params: kbScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetKBContent = async (
  propertyId = null,
  websiteId = null
) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/knowledge-base/get-content/`,
      { params: kbScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSaveKBContent = async (
  content,
  propertyId = null,
  websiteId = null
) => {
  try {
    const response = await privateApi.put(
      `property/chat-bot/knowledge-base/get-content/`,
      { content, ...kbScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot Analytics Summary ===============================================

export const handleGetChatbotSummary = async (params = {}) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/summary/`,
      { params }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatbotFunnel = async (params = {}) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/funnel/`,
      { params }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot Billing ===============================================

export const handleGetChatbotBillingMonthly = async (params = {}) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/billing/`,
      { params }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot Analytics ===============================================

export const handleGetChatbotLeads = async (
  windowParams = {},
  page = 1,
  pageSize = 10
) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/leads/`,
      {
        params: {
          ...windowParams,
          page,
          page_size: pageSize,
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatbotTopQuestions = async (windowParams = {}) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/top-questions/`,
      { params: windowParams }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatbotConversationDetail = async (
  conversationId,
  propertyId = null,
  websiteId = null
) => {
  try {
    const params = { conversation_id: conversationId };
    // Send exactly one scope; property takes precedence over website.
    if (propertyId) params.property_id = propertyId;
    else if (websiteId) params.website_id = websiteId;
    const response = await privateApi.get(
      `property/chat-bot/analytics/conversation-detail/`,
      { params }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ── Chatbot Live Agent ────────────────────────────────────────────────────

/**
 * Exactly one scope key, property first — matches windowParams in AnalyticsTab
 * and kbScopeParams above. Sending both would scope one thing to the property
 * and another to the website.
 */
const liveScopeParams = (propertyId = null, websiteId = null) => {
  if (propertyId) return { property_id: propertyId };
  if (websiteId) return { website_id: websiteId };
  return {};
};

export const handleGetLiveSessions = async (
  propertyId = null,
  websiteId = null
) => {
  try {
    const response = await privateApi.get(
      `property/chat-bot/analytics/live/sessions/`,
      { params: liveScopeParams(propertyId, websiteId) }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleLiveSessionPause = async (sessionId) => {
  try {
    const response = await privateApi.post(
      `property/chat-bot/analytics/live/pause/`,
      { session_id: sessionId }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleLiveSessionResume = async (sessionId) => {
  try {
    const response = await privateApi.post(
      `property/chat-bot/analytics/live/resume/`,
      { session_id: sessionId }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * WebSocket URL for the agent console.
 *
 * Must use `new URL(...).origin` — the socket lives at the server root, so a
 * naive string replace on BASE_URL would keep the /api/v1 prefix and 404.
 * Precedent for hand-authenticating a non-axios transport: the AI-insight
 * fetch below reads the token from GetCookies() the same way.
 */
export const buildAgentWsUrl = (token) => {
  let origin;
  try {
    origin = new URL(BASE_URL).origin;
  } catch {
    origin = window.location.origin;
  }
  const wsOrigin = origin.replace(/^http/, "ws");
  return `${wsOrigin}/ws/chat/agent/?token=${encodeURIComponent(token || "")}`;
};

// ── Service Catalog — Chatbot Config ──────────────────────────────────────

export const handleGetTopicKeywords = async (organizationId) => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/chatbot/topic-keywords/?organization_id=${organizationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePutTopicKeywords = async (keywords, organizationId) => {
  try {
    const response = await privateApi.put(
      `admin/service-catalog/chatbot/topic-keywords/`,
      { keywords, organization_id: organizationId }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetFollowupQuestions = async (organizationId) => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/chatbot/followup-questions/?organization_id=${organizationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePutFollowupQuestions = async (questions, organizationId) => {
  try {
    const response = await privateApi.put(
      `admin/service-catalog/chatbot/followup-questions/`,
      { questions, organization_id: organizationId }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ── Service Catalog — Chatbot Billing ────────────────────────────────────

export const handleGetChatbotBilling = async (
  organizationId,
  windowParams = {}
) => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/chatbot/billing/detail/`,
      { params: { organization_id: organizationId, ...windowParams } }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Service Catalog ===============================================

export const handleGetOrganizationList = async () => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/organization/list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateServiceCatalogOrganization = async (data) => {
  try {
    const response = await privateApi.post(
      `admin/service-catalog/organization/create/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetServiceCatalogOrganizationDetail = async (
  organizationId
) => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/organization/detail/?organization_id=${organizationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetChatbotProperties = async (organizationId) => {
  try {
    const response = await privateApi.get(
      `admin/service-catalog/organization/chatbot-properties/?organization_id=${organizationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// ── Service Catalog — Chatbot Websites (superuser CRUD) ──────────────────
const CHATBOT_WEBSITES_URL =
  "admin/service-catalog/organization/chatbot-websites/";

export const handleGetChatbotWebsites = async (organizationId) => {
  try {
    const response = await privateApi.get(
      `${CHATBOT_WEBSITES_URL}?organization_id=${organizationId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateChatbotWebsite = async ({
  organization_id,
  name,
  property_ids = [],
}) => {
  try {
    const response = await privateApi.post(CHATBOT_WEBSITES_URL, {
      organization_id,
      name,
      property_ids,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateChatbotWebsite = async ({
  organization_id,
  website_id,
  name,
  is_enabled,
  property_ids,
}) => {
  try {
    // Only send fields that were actually provided (partial PATCH).
    const body = { organization_id, website_id };
    if (name !== undefined) body.name = name;
    if (is_enabled !== undefined) body.is_enabled = is_enabled;
    if (property_ids !== undefined) body.property_ids = property_ids;
    const response = await privateApi.patch(CHATBOT_WEBSITES_URL, body);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteChatbotWebsite = async ({
  organization_id,
  website_id,
}) => {
  try {
    const response = await privateApi.delete(CHATBOT_WEBSITES_URL, {
      data: { organization_id, website_id },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleInsertServiceCatalogOrganizationPageAccess = async (
  data
) => {
  try {
    const response = await privateApi.post(
      `admin/service-catalog/organization/page-access/insert/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateServiceCatalogOrganizationAppfolioCredential = async (
  data
) => {
  try {
    const response = await privateApi.patch(
      `admin/service-catalog/organization/appfolio-credential/update/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetServiceCatalogRolesList = async () => {
  try {
    const response = await privateApi.get(`admin/service-catalog/roles/list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

// ======================================================== Chatbot AI Insights ===============================================

export const handleGetChatbotAiInsight = async (id, type, signal) => {
  const { accessToken } = GetCookies();
  const BASE_URL = process.env.REACT_APP_API_URL;
  return fetch(`${BASE_URL}/property/chat-bot/ai-insight/${id}/?type=${type}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    signal,
  });
};

export const handleGetPropertyOwnerReportRentRoll = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property-owner-report/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetRentRollSummary = async (queryString = "") => {
  try {
    const prefix = queryString ? `${queryString}&` : "";
    const response = await privateApi.get(
      `property/properties/property-owner-report/executive-summary/?${prefix}type=summary`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateRentRollSummary = async (data) => {
  try {
    const payload = { ...data };
    const response = await privateApi.post(
      `property/properties/property-owner-report/executive-summary/`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateRentRollSummary = async (data) => {
  try {
    const payload = { ...data };
    const response = await privateApi.patch(
      `property/properties/property-owner-report/executive-summary/`,
      payload
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// `scope` may be a legacy property id string, or an object with `property_id`
// and/or `property_group_id` (portfolio-wide summaries use the group scope).
export const handleDeleteRentRollSummary = async (id, scope) => {
  try {
    const params = new URLSearchParams({ id });
    if (typeof scope === "string") {
      if (scope) params.set("property_id", scope);
    } else if (scope && typeof scope === "object") {
      if (scope.property_id) params.set("property_id", scope.property_id);
      if (scope.property_group_id != null)
        params.set("property_group_id", String(scope.property_group_id));
    }
    const response = await privateApi.delete(
      `property/properties/property-owner-report/executive-summary/?${params.toString()}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetBudgetVarianceData = async (queryString = "") => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/data/${queryString ? `?${queryString}` : ""}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetBudgetVariancePeriodFiltersList = async () => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/period-filters-list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetBudgetVarianceComparisonFiltersList = async () => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/comparison-filters-list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetComparativePnlData = async (queryString = "") => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/comparative-pnl/${queryString ? `?${queryString}` : ""}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetAggregatedPnlData = async (queryString = "") => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/aggregated-pnl/${queryString ? `?${queryString}` : ""}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= BUDGET VARIANCE COMMENTS =======================================

export const handleGetBudgetVarianceRowComments = async (rowId) => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/get-row-comments/${rowId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetBudgetVariancePinnedComments = async (queryString) => {
  try {
    const response = await privateApi.get(
      `property/budget-variance/get-pinned-comments/${queryString ? `?${queryString}` : ""}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateBudgetVarianceComment = async (rowId, data) => {
  try {
    const response = await privateApi.post(
      `property/budget-variance/create-comment/${rowId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateBudgetVarianceComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/budget-variance/update-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePinBudgetVarianceComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `property/budget-variance/pin-comment/${commentId}`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteBudgetVarianceComment = async (commentId) => {
  try {
    const response = await privateApi.delete(
      `property/budget-variance/delete-comment/${commentId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//=============================================== OWNER REPORT CUSTOM COLUMNS ===============================================

export const handleCreateOwnerReportColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property-owner-report/create-column/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleListOwnerReportColumns = async (propertyId) => {
  try {
    const response = await privateApi.get(
      `property/properties/property-owner-report/list-column/?property_id=${propertyId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSetOwnerReportColumnValue = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property-owner-report/set-column-value/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateOwnerReportColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property-owner-report/update-column/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteOwnerReportColumn = async (data) => {
  try {
    const response = await privateApi.post(
      `property/properties/property-owner-report/delete-column/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= GL CODE MANAGEMENT =======================================

export const handleGetGLCodeList = async () => {
  try {
    const response = await privateApi.get(`property/units/gl-code/list/`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateGLCode = async (data) => {
  try {
    const response = await privateApi.post(
      `property/units/gl-code/create/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateGLCode = async (data) => {
  try {
    const response = await privateApi.put(
      `property/units/gl-code/update/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteGLCode = async (data) => {
  try {
    const response = await privateApi.delete(`property/units/gl-code/delete/`, {
      data,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

//======================================= WORK ORDER LABOR SUMMARY =======================================

const WORK_ORDER_LABOR_PREFIX = "property/work-order-labor";

const withQuery = (path, queryString) =>
  `${path}${queryString ? `?${queryString}` : ""}`;

export const handleGetWorkOrderLaborFilterOptions = async (
  queryString = ""
) => {
  try {
    const response = await privateApi.get(
      withQuery(`${WORK_ORDER_LABOR_PREFIX}/filter-options/`, queryString)
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetWorkOrderLaborAnalytics = async (queryString = "") => {
  try {
    const response = await privateApi.get(
      withQuery(`${WORK_ORDER_LABOR_PREFIX}/analytics/`, queryString)
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetWorkOrderLaborList = async (queryString = "") => {
  try {
    const response = await privateApi.get(
      withQuery(`${WORK_ORDER_LABOR_PREFIX}/list/`, queryString)
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSetWorkOrderLaborHourlyRate = async (data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/set-hourly-rate/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// --- Work Order Labor: custom fields ---

export const handleListWorkOrderLaborCustomFields = async () => {
  try {
    const response = await privateApi.get(
      `${WORK_ORDER_LABOR_PREFIX}/custom-fields/list/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateWorkOrderLaborCustomField = async (data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/custom-fields/create/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateWorkOrderLaborCustomField = async (data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/custom-fields/update/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSetWorkOrderLaborCustomFieldValue = async (data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/custom-fields/set-value/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleSoftDeleteWorkOrderLaborCustomField = async (data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/custom-fields/soft-delete/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

// --- Work Order Labor: comments (row-based, mirrors Budget Variance) ---

export const handleGetWorkOrderLaborRowComments = async (laborRowId) => {
  try {
    const response = await privateApi.get(
      `${WORK_ORDER_LABOR_PREFIX}/get-row-comments/${laborRowId}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleGetWorkOrderLaborPinnedComments = async () => {
  try {
    const response = await privateApi.get(
      `${WORK_ORDER_LABOR_PREFIX}/get-pinned-comments/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleCreateWorkOrderLaborComment = async (laborRowId, data) => {
  try {
    const response = await privateApi.post(
      `${WORK_ORDER_LABOR_PREFIX}/create-comment/${laborRowId}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleUpdateWorkOrderLaborComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `${WORK_ORDER_LABOR_PREFIX}/update-comment/${commentId}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handlePinWorkOrderLaborComment = async (commentId, data) => {
  try {
    const response = await privateApi.patch(
      `${WORK_ORDER_LABOR_PREFIX}/pin-comment/${commentId}/`,
      data
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const handleDeleteWorkOrderLaborComment = async (commentId) => {
  try {
    const response = await privateApi.delete(
      `${WORK_ORDER_LABOR_PREFIX}/delete-comment/${commentId}/`
    );
    return response;
  } catch (error) {
    throw error;
  }
};
